import React, { useState, useEffect } from 'react';
import { X, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../hooks/useTranslation';
import type { Database } from '../lib/database.types';

type ServiceRequest = Database['public']['Tables']['service_requests']['Row'] & {
  car: Database['public']['Tables']['cars']['Row'] | null;
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    username: string;
    email: string;
    phone: string | null;
    secondary_phone: string | null;
  } | null;
  images: Array<{ image_url: string }>;
  services: Array<{
    service: {
      id: string;
      name: string;
      category_id: string;
      category: {
        id: string;
        name: string;
      };
    };
  }>;
};

interface ServiceRequestReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestUpdated: () => void;
  request: ServiceRequest;
}

type ValidStatus = 'pending' | 'in_progress' | 'completed' | 'rejected';

type GroupedServices = {
  [key: string]: {
    categoryName: string;
    services: string[];
  };
};

export function ServiceRequestReviewModal({ 
  isOpen, 
  onClose, 
  onRequestUpdated,
  request 
}: ServiceRequestReviewModalProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState(request.quote_amount?.toString() || '');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [customerProfile, setCustomerProfile] = useState<ServiceRequest['profile']>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [requestDetails, setRequestDetails] = useState<ServiceRequest | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  useEffect(() => {
    let isMounted = true;

    const fetchRequestDetails = async () => {
      try {
        setError(null);
        const { data, error } = await supabase
          .from('service_requests')
          .select(`
            *,
            car:cars (*),
            profile:profiles (
              id, first_name, last_name, username, email, phone, secondary_phone
            ),
            images:service_request_images (
              image_url
            ),
            services:service_request_services (
              service:services (
                id, name, category_id,
                category:service_categories (
                  id, name
                )
              )
            )
          `)
          .eq('id', request.id)
          .single();

        if (error) throw error;

        if (isMounted && data) {
          setRequestDetails(data);
          setCustomerProfile(data.profile);
          setError(null);
          setRetryCount(0);
        }
      } catch (err) {
        console.error('Error fetching request details:', err);
        if (isMounted && retryCount < maxRetries) {
          setRetryCount(prev => prev + 1);
          setTimeout(fetchRequestDetails, Math.min(1000 * Math.pow(2, retryCount), 5000));
        } else if (isMounted) {
          setError('Failed to load request details. Please try again.');
        }
      } finally {
        if (isMounted) {
          setLoadingProfile(false);
        }
      }
    };

    if (isOpen) {
      setLoadingProfile(true);
      fetchRequestDetails();
    }

    return () => {
      isMounted = false;
    };
  }, [isOpen, request.id, retryCount]);

  const handleStatusUpdate = async (newStatus: ValidStatus) => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const updates: {
        status: ValidStatus;
        rejection_reason?: string | null;
        quote_amount?: number | null;
      } = {
        status: newStatus
      };

      if (newStatus === 'rejected') {
        if (!rejectionReason) {
          setError('Please provide a rejection reason');
          setLoading(false);
          return;
        }
        updates.rejection_reason = rejectionReason;
      } else {
        updates.rejection_reason = null;
      }

      if (newStatus === 'in_progress') {
        if (!quoteAmount || isNaN(parseFloat(quoteAmount))) {
          setError('Please provide a valid quote amount');
          setLoading(false);
          return;
        }
        updates.quote_amount = parseFloat(quoteAmount);
      }

      const { error: updateError } = await supabase
        .from('service_requests')
        .update(updates)
        .eq('id', request.id);

      if (updateError) throw updateError;

      onRequestUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating service request:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const groupServicesByCategory = (services: ServiceRequest['services']): GroupedServices => {
    return services.reduce((acc: GroupedServices, { service }) => {
      const categoryId = service.category.id;
      if (!acc[categoryId]) {
        acc[categoryId] = {
          categoryName: service.category.name,
          services: []
        };
      }
      acc[categoryId].services.push(service.name);
      return acc;
    }, {});
  };

  if (!isOpen) return null;

  if (loadingProfile) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 text-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading request details...</p>
        </div>
      </div>
    );
  }

  const currentRequest = requestDetails || request;
  const groupedServices = groupServicesByCategory(currentRequest.services);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Review Service Request
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Customer Information */}
        <div>
          <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
            Customer Information
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            {customerProfile ? (
              <div className="space-y-2">
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Name:</span> {customerProfile.first_name} {customerProfile.last_name}
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Username:</span> @{customerProfile.username}
                </p>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Email:</span> {customerProfile.email}
                </p>
                {customerProfile.phone && (
                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="font-semibold">Phone:</span> {customerProfile.phone}
                  </p>
                )}
                {customerProfile.secondary_phone && (
                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="font-semibold">Secondary Phone:</span> {customerProfile.secondary_phone}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-300">
                {error ? 'Failed to load customer information' : 'No customer information available'}
              </p>
            )}
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
            Vehicle Information
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            {currentRequest.car ? (
              <>
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="font-semibold">Vehicle:</span> {currentRequest.car.brand} {currentRequest.car.model} ({currentRequest.car.year})
                </p>
                {currentRequest.car.plate_state && currentRequest.car.plate_code && currentRequest.car.plate_number && (
                  <p className="text-gray-600 dark:text-gray-300">
                    <span className="font-semibold">Plate:</span> {currentRequest.car.plate_state} {currentRequest.car.plate_code} {currentRequest.car.plate_number}
                  </p>
                )}
              </>
            ) : (
              <p className="text-gray-600 dark:text-gray-300">Vehicle information not available</p>
            )}
          </div>
        </div>

        {/* Services */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
            Requested Services
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-4">
            {Object.entries(groupedServices).map(([categoryId, category]) => (
              <div key={categoryId} className="border-b dark:border-gray-600 last:border-0 pb-4 last:pb-0">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                  {category.categoryName}
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {category.services.map((service, index) => (
                    <li key={index} className="text-gray-600 dark:text-gray-300">
                      {service}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
            Description
          </h3>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <p className="text-gray-600 dark:text-gray-300">
              {currentRequest.description || 'No description provided'}
            </p>
          </div>
        </div>

        {/* Images */}
        {currentRequest.images && currentRequest.images.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
              Attached Images
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {currentRequest.images.map((image, index) => (
                <div key={index} className="relative group">
                  <div 
                    className="aspect-w-3 aspect-h-2 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 cursor-pointer"
                    onClick={() => setSelectedImage(image.image_url)}
                  >
                    <img
                      src={image.image_url}
                      alt={`Service Request Image ${index + 1}`}
                      className="object-cover w-full h-full transition-transform duration-200 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <ExternalLink className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
            <div className="relative max-w-7xl w-full h-full p-4">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300"
              >
                <X className="w-8 h-8" />
              </button>
              <div className="h-full flex items-center justify-center">
                <img
                  src={selectedImage}
                  alt="Full size"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          </div>
        )}

        {/* Action Section */}
        {!isRejecting ? (
          <div className="space-y-4 mt-6">
            {currentRequest.status === 'pending' && (
              <>
                {/* Quote Amount Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Quote Amount (AED)
                  </label>
                  <input
                    type="number"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Enter quote amount"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setIsRejecting(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleStatusUpdate('in_progress')}
                    disabled={!quoteAmount || isNaN(parseFloat(quoteAmount))}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    Approve & Start
                  </button>
                </div>
              </>
            )}

            {currentRequest.status === 'in_progress' && (
              <div className="flex justify-end">
                <button
                  onClick={() => handleStatusUpdate('completed')}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Mark as Completed
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 mt-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rejection Reason
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Please provide a reason for rejection"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsRejecting(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusUpdate('rejected')}
                disabled={!rejectionReason}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                Confirm Rejection
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}